import { useMutation } from '@tanstack/react-query';

import {
  deleteV1RequestBusinessPartnerById,
  deleteV1ServiceAgreementById,
  postV1ServiceAgreementByIdAccept,
  postV1ServiceAgreementByIdDecline,
  postV1ServiceAgreementWithCustomer,
  type ServiceAgreementDeclinePostBody,
  type ServiceAgreementWithCustomerPostBody,
} from '../../api/generated/pm';
import { invalidateServiceAgreementsWithProvider } from '../Settings/Dealers/dealers.queries';
import {
  invalidateCustomerAgreementLists,
  invalidateServiceAgreementWithId,
} from './customers.queries';

/**
 * Clears customer list, dealer list, and single-agreement caches after an
 * accept/decline decision. Same-tab navigations rely on this; the review tab's
 * QueryClient never reaches the list tab opened via `window.open` — that tab
 * refreshes from its own visibility listener instead.
 */
export const invalidateServiceAgreementDecisionCaches = () =>
  Promise.all([
    invalidateCustomerAgreementLists(),
    invalidateServiceAgreementsWithProvider(),
    invalidateServiceAgreementWithId(),
  ]);

/**
 * Mutation hooks for the customer side of Service Agreements. All POST/DELETE;
 * the backend does not check `If-Match` on these, so no header handling applies.
 */

export const useCreateServiceAgreementWithCustomer = () =>
  useMutation({
    mutationFn: async (payload: ServiceAgreementWithCustomerPostBody) => {
      const { data } = await postV1ServiceAgreementWithCustomer({
        body: payload,
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      invalidateCustomerAgreementLists();
    },
  });

export const useDeleteServiceAgreement = () =>
  useMutation({
    mutationFn: async (id: string) => {
      await deleteV1ServiceAgreementById({ path: { id }, throwOnError: true });
    },
    onSuccess: () => {
      invalidateServiceAgreementsWithProvider();
    },
  });

/** Withdraws a pending agreement request for a not-yet-existing customer. */
export const useCancelServiceAgreementRequest = () =>
  useMutation({
    mutationFn: async (id: string) => {
      await deleteV1RequestBusinessPartnerById({ path: { id }, throwOnError: true });
    },
    onSuccess: () => {
      invalidateServiceAgreementsWithProvider();
      invalidateCustomerAgreementLists();
    },
  });

export const useAcceptServiceAgreement = () =>
  useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await postV1ServiceAgreementByIdAccept({ path: { id }, throwOnError: true });
    },
    onSuccess: () => {
      invalidateServiceAgreementDecisionCaches();
    },
  });

export const useDeclineServiceAgreement = () =>
  useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: ServiceAgreementDeclinePostBody;
    }) => {
      await postV1ServiceAgreementByIdDecline({ path: { id }, body: payload, throwOnError: true });
    },
    onSuccess: () => {
      invalidateServiceAgreementDecisionCaches();
    },
  });
